/**
 * Validates multiple international phone numbers separated by commas
 * Each phone number must be in international format starting with +
 * followed by 7-15 digits (country code + number)
 *
 * Valid examples:
 * - "+3466655544"
 * - "+3466656500,+3423623623623"
 * - "+7298375298375,+72983759287395"
 *
 * Invalid examples:
 * - "4363463" (no + prefix)
 * - "sdgsdgsdg" (not numeric)
 * - "23634634,346346" (no + prefix)
 * - "32463463,34634634," (trailing comma)
 * - "sodjhgsjhdg,jhkjsdhglksjdh," (not numeric)
 */

// Regex for a single international phone number:
// ^ - start of string
// \+ - literal plus sign
// [1-9] - first digit (1-9, no leading zeros for country codes)
// \d{6,14} - 6 to 14 additional digits (total 7-15 digits after +)
// $ - end of string
const singlePhoneRegex = /^\+[1-9]\d{6,14}$/;

// Regex for multiple phone numbers separated by commas:
// ^ - start of string
// \+[1-9]\d{6,14} - first phone number
// (?:,\+[1-9]\d{6,14})* - zero or more additional phone numbers preceded by comma
// $ - end of string
const multiplePhoneRegex = /^\+[1-9]\d{6,14}(?:,\+[1-9]\d{6,14})*$/;

export const validateMultiplePhoneNumbers = (phoneNumbers: string): boolean => {
  if (!phoneNumbers || phoneNumbers.trim() === "") {
    return false;
  }

  // Check if the input has leading or trailing spaces (should be rejected)
  if (phoneNumbers !== phoneNumbers.trim()) {
    return false;
  }

  // Check if the entire string matches the multiple phone pattern
  if (!multiplePhoneRegex.test(phoneNumbers)) {
    return false;
  }

  // Additional validation: split by comma and validate each phone individually
  const phoneArray = phoneNumbers.split(",");

  // Check for empty phones after split (this catches cases like trailing commas)
  if (phoneArray.some((phone) => phone.trim() === "")) {
    return false;
  }

  // Validate each phone number individually
  return phoneArray.every((phone) => singlePhoneRegex.test(phone));
};
